// src/component/AdminPanelOrdersDetails.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useAdminApi } from "./adminApi";

/* ---------- UI утилиты ---------- */
const Spinner = ({ className = "h-4 w-4" }) => (
  <span className={`inline-block ${className} border-2 border-black/20 border-t-black rounded-full animate-spin`} />
);

const StatusBadge = ({ status }) => {
  if (!status) return <span className="px-2 py-0.5 rounded-full text-xs ring-1 ring-gray-200 bg-gray-50 text-gray-700">—</span>;
  const code = (status.code || status.title || "").toString().toLowerCase();
  const map = {
    new:        "bg-blue-50  text-blue-700  ring-blue-200",
    pending:    "bg-amber-50 text-amber-700 ring-amber-200",
    processing: "bg-indigo-50 text-indigo-700 ring-indigo-200",
    paid:       "bg-emerald-50 text-emerald-700 ring-emerald-200",
    shipped:    "bg-cyan-50 text-cyan-700 ring-cyan-200",
    delivered:  "bg-green-50 text-green-700 ring-green-200",
    canceled:   "bg-rose-50 text-rose-700 ring-rose-200",
    cancelled:  "bg-rose-50 text-rose-700 ring-rose-200",
    refunded:   "bg-gray-100 text-gray-700 ring-gray-300",
  };
  const cls = map[code] || "bg-gray-50 text-gray-700 ring-gray-200";
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ring-1 ${cls}`}>{status.title || status.code}</span>;
};

const fmtDate = (d) => { try { return d ? new Date(d).toLocaleString("ru-RU") : "—"; } catch { return d; } };
const fmtMoney = (n) => Number(n || 0).toLocaleString("ru-RU") + " ₽";

export default function AdminPanelOrdersDetails({ orderId, setView }) {
  const api = useAdminApi();

  const [order, setOrder] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [statusCode, setStatusCode] = useState("new");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveOk, setSaveOk] = useState(false);
  const [saveErr, setSaveErr] = useState("");

  /* ---------- Загрузка заказа ---------- */
  useEffect(() => {
    if (!orderId) return;
    let off = false;
    (async () => {
      try {
        const { data, status } = await api.get(`admin/orders/${orderId}/`);
        if (!off && status === 200 && data) {
          setOrder(data);
          setStatusCode(data.status?.code || "new");
        }
      } finally {
        if (!off) setLoading(false);
      }
    })();
    return () => { off = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  /* ---------- Справочник статусов ---------- */
  useEffect(() => {
    (async () => {
      const { data, status } = await api.raw("/api/orders/statuses/");
      if (status === 200 && Array.isArray(data)) setStatuses(data);
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------- Сохранение статуса ---------- */
  const saveStatus = async () => {
    if (!order || saving) return;
    setSaving(true); setSaveOk(false); setSaveErr("");
    try {
      const { data, status } = await api.raw(`/api/orders/admin/${orderId}/change_status/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: statusCode }),
      });

      if (status === 200 && data?.new_status) {
        setOrder((prev) => prev ? ({ ...prev, status: data.new_status }) : prev);
        setStatusCode(data.new_status.code);
        setSaveOk(true);
        // сразу просим список обновиться (слушает AdminPanelOrders)
        try { window.dispatchEvent(new Event("orders:refresh")); } catch {}
        setTimeout(() => setSaveOk(false), 1500);
      } else {
        setSaveErr("Не удалось сохранить статус");
      }
    } catch {
      setSaveErr("Ошибка сети");
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Назад в список (мягко) ---------- */
  const backToList = () => {
    try {
      // отмечаем активный таб «orders» (если админка читает ?tab=)
      const url = new URL(window.location.href);
      url.searchParams.set("tab", "orders");
      window.history.replaceState(null, "", url.toString());
      // и просим список обновиться
      window.dispatchEvent(new Event("orders:refresh"));
    } catch {}
    setView?.(null);
  };

  /* ---------- Производные ---------- */
  const items = useMemo(() => order?.items || order?.positions || order?.order_items || [], [order]);
  const totalAmount = useMemo(() => {
    if (order?.total_price != null) return Number(order.total_price);
    if (!Array.isArray(items)) return 0;
    return items.reduce((acc, it) => acc + (Number(it.price ?? it.unit_price ?? 0) * Number(it.quantity ?? it.qty ?? 0)), 0);
  }, [order, items]);

  if (loading) {
    return (
      <div className="p-6 rounded-xl ring-1 ring-gray-200 bg-white shadow-sm">
        <div className="flex items-center gap-3 text-gray-600">
          <Spinner className="h-5 w-5" /> Загрузка заказа…
        </div>
      </div>
    );
  }
  if (!order) {
    return (
      <div className="p-6 rounded-xl ring-1 ring-rose-200 bg-rose-50 text-rose-700">
        Заказ не найден.
      </div>
    );
  }

  /* ---------- Разметка ---------- */
  return (
    <section className="space-y-4">
      {/* Тулбар */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={backToList}
            className="inline-flex items-center gap-2 rounded-md bg-white ring-1 ring-gray-300 px-3 py-2 text-sm hover:bg-gray-50"
            title="Назад к списку"
          >
            ← Назад к списку
          </button>
          <span className="text-sm text-gray-500">№ {order.order_number || order.id}</span>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="hidden sm:inline text-gray-500">Статус:</span>
          <StatusBadge status={order.status} />
          <span className="text-gray-400">·</span>
          <span className="text-gray-500">Создан: {fmtDate(order.created_date || order.created_at)}</span>
        </div>
      </div>

      {/* Карточка: клиент + доставка */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 p-4 rounded-xl ring-1 ring-gray-200 bg-white shadow-sm">
          <h3 className="font-semibold mb-3">Клиент</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div><span className="text-gray-500">Имя: </span><span className="font-medium">{order.first_name || "—"}</span></div>
            <div><span className="text-gray-500">Фамилия: </span><span className="font-medium">{order.last_name || "—"}</span></div>
            <div><span className="text-gray-500">E-mail: </span><span className="font-medium">{order.email || "—"}</span></div>
            <div><span className="text-gray-500">Телефон: </span><span className="font-medium">{order.phone || "—"}</span></div>
          </div>

          <h3 className="font-semibold mt-5 mb-2">Получение</h3>
          <div className="text-sm">
            <div><span className="text-gray-500">Способ: </span><span className="font-medium">
              {order.delivery_method === "pickup" ? "Самовывоз" : "Доставка"}
            </span></div>
            {order.delivery_method === "pickup" && (
              <div><span className="text-gray-500">Магазин: </span><span className="font-medium">{order.store_location || "—"}</span></div>
            )}
            {order.delivery_method === "delivery" && (
              <div><span className="text-gray-500">Адрес: </span><span className="font-medium break-words">{order.address || "—"}</span></div>
            )}
            {order.comment && (
              <div className="mt-2"><span className="text-gray-500">Комментарий: </span><span className="font-medium break-words">{order.comment}</span></div>
            )}
          </div>
        </div>

        <div className="p-4 rounded-xl ring-1 ring-gray-200 bg-white shadow-sm">
          <h3 className="font-semibold mb-3">Статус заказа</h3>
          <div className="space-y-3">
            <select
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 disabled:opacity-50"
              value={statusCode}
              onChange={(e) => setStatusCode(e.target.value)}
              disabled={saving}
            >
              {statuses.map((s) => (
                <option key={s.id} value={s.code}>{s.title}</option>
              ))}
            </select>

            <button
              type="button"
              onClick={saveStatus}
              disabled={saving}
              className={`w-full inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold shadow
                         ${saving ? "bg-orange-300 cursor-wait text-white" : "bg-orange-500 hover:bg-orange-600 text-white"}`}
            >
              {saving && <Spinner className="h-4 w-4 border-white/60 border-t-white" />}
              {saving ? "Сохраняем…" : "Сохранить"}
            </button>

            {saveOk && (
              <div className="text-green-700 bg-emerald-50 ring-1 ring-emerald-200 rounded-md px-3 py-2 text-xs">
                ✓ Сохранено
              </div>
            )}
            {saveErr && (
              <div className="text-rose-700 bg-rose-50 ring-1 ring-rose-200 rounded-md px-3 py-2 text-xs">
                {saveErr}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Состав заказа */}
      <div className="p-4 rounded-xl ring-1 ring-gray-200 bg-white shadow-sm">
        <h3 className="font-semibold mb-3">Состав заказа</h3>

        {Array.isArray(items) && items.length > 0 ? (
          <div className="divide-y">
            {items.map((it) => (
              <div key={it.id} className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm">
                <div className="min-w-0">
                  <div className="font-medium truncate">{it.product_name || it.product || `Товар #${it.product_id || it.id}`}</div>
                  <div className="text-xs text-gray-500 truncate">
                    {it.sku ? `SKU: ${it.sku} · ` : ""}Кол-во: {Number(it.quantity ?? it.qty ?? 0)}
                  </div>
                </div>
                <div className="shrink-0 font-semibold">{fmtMoney(Number(it.price ?? it.unit_price ?? 0))}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-gray-500">Позиции не найдены.</div>
        )}

        <div className="mt-4 flex items-center justify-between text-sm">
          <div className="text-gray-500">Итого</div>
          <div className="text-lg font-bold text-gray-800">{fmtMoney(totalAmount)}</div>
        </div>
      </div>
    </section>
  );
}
